import Switch from '@mui/material/Switch'
import { styled } from '@mui/material'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import {
  ArchClient,
  ArchCourseForTable,
  ArchIntake,
  ArchProgram,
  ArchTopic,
} from 'apis/entities/architecture.entity'
import SidebarLayout from 'components/SidebarLayout'
import Emitter, { Events } from 'core/emitter'
import { useArchitectureApi } from 'providers/ArchitectureApiProvider'
import { useCompositeAuth, Role } from 'providers/CompositeAuthProvider'
import ArchManageView from 'views/ArchManageView'
import ArchReadOnlyView from 'views/ArchReadOnlyView'
import DropDownMenu, {
  DropDownItem,
  DropDownMenuRefType,
} from 'views/DropDownMenu'
import WidgetArchCopy from 'views/WidgetArchCopy'
import WidgetArchCourseDetailsView from 'views/WidgetArchCourseDetailsView'
import WidgetArchEditTopic from 'views/WidgetArchEditTopic'
import WidgetArchManageClient from 'views/WidgetArchManageClient'
import WidgetArchUpload from 'views/WidgetArchUpload'
import WidgetUnassignedAssessments from 'views/WidgetUnassignedAssessments'
import {
  ArchActionType,
  getArchBreadcrumb,
  getEntityLabel,
  isUnswOnlineClient,
  sortIntakeOptionsByCreatedAt,
} from 'utils/ArchUtil'
import { StyleUtil } from 'utils/StyleUtil'
import { ToastUtil } from 'utils/ToastUtil'

const ArchSwitch = styled(Switch, {
  shouldForwardProp: (prop) => prop !== '$checked',
})<{ $checked: boolean }>(({ $checked }) => ({
  padding: 9,
  width: 54,
  '& .MuiButtonBase-root': {
    '&.Mui-checked': {
      transform: 'translateX(16px)',
    },
    '& .MuiSwitch-thumb': {
      boxShadow: 'none',
      width: 12,
      height: 12,
      margin: 4,
      ...(!$checked && {
        backgroundColor: '#8A68C8',
      }),
    },
  },
  '& .MuiSwitch-track': {
    borderRadius: 22 / 2,
    border: '1px solid black',
    ...($checked
      ? { background: '#8A68C8', opacity: '1 !important' }
      : { background: 'white', opacity: '1 !important' }),
    '&::before, &::after': {
      content: '""',
      position: 'absolute',
      top: '50%',
      transform: 'translateY(-50%)',
      width: 16,
      height: 16,
    },
    '&::before': { left: 12 },
    '&::after': { right: 12 },
  },
}))

type FilteredCourseDict = {
  [clientId: string]: ArchCourseForTable
}

const ClientOptionShowAll = {
  id: 'all',
  name: 'All clients',
  value: 'all',
  isLabel: false,
}

const ProgramOptionShowAll = {
  id: 'all',
  name: 'Program',
  value: 'all',
  isLabel: false,
}

const IntakeOptionShowAll = {
  id: 'all',
  name: 'All cohorts',
  value: 'all',
  isLabel: false,
}

function ArchitecturePage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { clientId, mode } = useParams()
  const refCurrentClientId = useRef<string | undefined>(clientId)
  // console.log('clientId', clientId)
  // console.log('mode', mode)
  const { isLogged, getRole } = useCompositeAuth()
  const { getArchClients, getArchForTable, getArchClient, cloneArchProgram } =
    useArchitectureApi()
  const locationState = location.state as { isEnterprise?: boolean } | null
  const [enterpriseToggle, setEnterpriseToggle] = useState(
    typeof locationState?.isEnterprise === 'boolean'
      ? locationState.isEnterprise
      : true,
  )
  const [isFetchingClients, setIsFetchingClients] = useState(false)
  const [isFetchingArchitectures, setIsFetchingArchitectures] = useState(false)
  const [clients, setClients] = useState<ArchClient[]>([])
  const [selectedClient, setSelectedClient] = useState<ArchClient | null>(null)
  const [selectedProgram, setSelectedProgram] = useState<ArchProgram | null>(
    null,
  )
  const [selectedIntake, setSelectedIntake] = useState<ArchIntake | null>(null)

  const refSelectedClient = useRef<ArchClient | null>(null)
  const refSelectedProgram = useRef<ArchProgram | null>(null)
  const refSelectedIntake = useRef<ArchIntake | null>(null)

  const [clientOptions, setClientOptions] = useState<any>([ClientOptionShowAll])
  const [programOptions, setProgramOptions] = useState<any>([
    ProgramOptionShowAll,
  ])
  const [intakeOptions, setIntakeOptions] = useState<any>([
    {
      ...IntakeOptionShowAll,
      name: enterpriseToggle ? 'Cohort' : 'Hexamester',
    },
  ])
  const refHasAppliedModeDefaultSelection = useRef(false)
  const refDeepLinkProgramId = useRef(
    new URLSearchParams(location.search).get('programId'),
  )

  enum AdminMenu {
    View = 'view',
    Manage = 'manage',
  }

  function selectAllClients() {
    onSelectClient(ClientOptionShowAll)
    refDropDownMenuClients.current?.setSelectedItemId(ClientOptionShowAll.id)
  }

  function selectUnswOnlineClient(forceViewMode: boolean = false) {
    const unswOnlineClient = clients.find((client) =>
      isUnswOnlineClient(client),
    )
    if (unswOnlineClient) {
      onSelectClient(
        {
          id: unswOnlineClient.id,
          name: unswOnlineClient.name,
          value: unswOnlineClient.id,
          isLabel: false,
        },
        { forceViewMode },
      )
      refDropDownMenuClients.current?.setSelectedItemId(unswOnlineClient.id)
    }
  }

  useEffect(() => {
    if (typeof locationState?.isEnterprise === 'boolean') {
      setEnterpriseToggle(locationState.isEnterprise)
    }
  }, [locationState?.isEnterprise])

  useEffect(() => {
    setProgramOptions((prev: any[]) => [
      { ...prev[0], name: enterpriseToggle ? 'Program' : 'Course' },
      ...prev.slice(1),
    ])
    setIntakeOptions((prev: any[]) => [
      { ...prev[0], name: enterpriseToggle ? 'Cohort' : 'Hexamester' },
      ...prev.slice(1),
    ])
    if (!refHasAppliedModeDefaultSelection.current || clients.length === 0) {
      return
    }
    if (enterpriseToggle) {
      selectAllClients()
    } else {
      selectUnswOnlineClient(selectedAdminMenu === AdminMenu.Manage)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enterpriseToggle])

  // Apply the default client selection when clients load for the first time.
  useEffect(() => {
    if (clients.length === 0 || refHasAppliedModeDefaultSelection.current) {
      return
    }
    const hasRouteSelectedClient =
      !!refCurrentClientId.current &&
      clients.some((client) => client.id === refCurrentClientId.current)

    if (hasRouteSelectedClient) {
      refHasAppliedModeDefaultSelection.current = true
      return
    }

    if (enterpriseToggle) {
      selectAllClients()
    } else if (selectedClient === null) {
      selectUnswOnlineClient()
    }
    refHasAppliedModeDefaultSelection.current = true
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clients])

  const isAdmin = getRole() === Role.Admin || getRole() === Role.SuperAdmin

  const [courses, setCourses] = useState<ArchCourseForTable[]>([])
  const refCourses = useRef<ArchCourseForTable[]>([]) // for cache
  const refFilteredCourses = useRef<FilteredCourseDict>({}) // for cache
  const [showCourseDetails, setShowCourseDetails] = useState(false)
  const [courseDetails, setCourseDetails] = useState<ArchCourseForTable | null>(
    null,
  )

  const [showManageClient, setShowManageClient] = useState(false)
  const [showUnassignedAssignments, setShowUnassignedAssignments] =
    useState(false)
  const [selectedCourseId, setSelectedCourseId] = useState<string | undefined>(
    undefined,
  )

  const [selectedAdminMenu, setSelectedAdminMenu] = useState<AdminMenu>(
    isAdmin && mode === AdminMenu.Manage ? AdminMenu.Manage : AdminMenu.View,
  )

  const [showWidgetArchCopy, setShowWidgetArchCopy] = useState(false)
  // ids for copy, program and intake are single, course, module, topic are multiple
  const [copyProgramId, setCopyProgramId] = useState<string | undefined>(
    undefined,
  )
  const [copyIntakeId, setCopyIntakeId] = useState<string | undefined>(
    undefined,
  )
  const [copyCourseIds, setCopyCourseIds] = useState<string[] | undefined>(
    undefined,
  )
  const [copyModuleIds, setCopyModuleIds] = useState<string[] | undefined>(
    undefined,
  )
  const [copyTopicIds, setCopyTopicIds] = useState<string[] | undefined>(
    undefined,
  )
  const [copyQuizIds, setCopyQuizIds] = useState<string[] | undefined>(
    undefined,
  )

  const [showWidgetEditTopic, setShowWidgetEditTopic] = useState(false)
  const [editTopic, setEditTopic] = useState<ArchTopic | undefined>(undefined)
  const [editTopicPosition, setEditTopicPosition] = useState<
    { x: number; y: number } | undefined
  >(undefined)

  const [showWidgetUpload, setShowWidgetUpload] = useState(false)
  const [uploadInakeId, setUploadIntakeId] = useState<string | undefined>(
    undefined,
  )
  const architectureDropdownClassName = enterpriseToggle
    ? 'arch-page-dropdown'
    : 'arch-page-dropdown-unswo'

  // ref for dropdown menu
  const refDropDownMenuClients = useRef<DropDownMenuRefType>(null)
  const refDropDownMenuPrograms = useRef<DropDownMenuRefType>(null)
  const refDropDownMenuIntakes = useRef<DropDownMenuRefType>(null)

  const setCoursesForTable = (courses: ArchCourseForTable[]) => {
    // flatten courses
    setCourses(courses)
  }

  const updateProgramOptions = useCallback(() => {
    // set all program options by distinct program names
    const tmpProgramOptions: any[] = []

    // all clients
    if (refSelectedClient.current === null) {
      let allCourses = refCourses.current
      allCourses.forEach((course) => {
        if (!tmpProgramOptions.find((p) => p.name === course.programName)) {
          tmpProgramOptions.push({
            id: course.programId,
            name: course.programName,
            value: course.programId,
          })
        }
      })
    } else {
      // filter by selected client, use selected client's object
      const programs = refSelectedClient.current?.programs ?? []
      programs.forEach((program) => {
        if (!tmpProgramOptions.find((p) => p.name === program.name)) {
          tmpProgramOptions.push({
            id: program.id,
            name: program.name,
            value: program.id,
          })
        }
      })
    }
    // console.log(refSelectedClient.current)
    // console.log('tmpProgramOptions', tmpProgramOptions)

    // sort program options by alphabet ascending
    tmpProgramOptions.sort((a, b) => {
      if (a.name < b.name) {
        return -1
      }
      if (a.name > b.name) {
        return 1
      }
      return 0
    })

    setProgramOptions([
      {
        ...ProgramOptionShowAll,
        name: enterpriseToggle ? 'Program' : 'Course',
      },
      ...tmpProgramOptions,
    ])
  }, [enterpriseToggle])

  const updateIntakeOptions = useCallback(() => {
    // set all intake options by distinct intake names
    const tmpIntakeOptions: any[] = []

    // all clients
    if (refSelectedClient.current === null) {
      let allCourses = refCourses.current
      allCourses.forEach((course) => {
        if (!tmpIntakeOptions.find((i) => i.name === course.intakeName)) {
          tmpIntakeOptions.push({
            id: course.intakeId,
            name: course.intakeName,
            value: course.intakeId,
            createdAt: course.intakeCreatedAt,
          })
        }
      })
    } else {
      // filter by selected client, use selected client's object
      const programs = refSelectedClient.current?.programs ?? []
      programs.forEach((program) => {
        const intakes = program.intakes ?? []
        intakes.forEach((intake) => {
          if (!tmpIntakeOptions.find((i) => i.name === intake.name)) {
            tmpIntakeOptions.push({
              id: intake.id,
              name: intake.name,
              value: intake.id,
              createdAt: intake.createdAt,
            })
          }
        })
      })
    }

    // sort intake options by date first, otherwise by alphabet ascending
    const sortedTmpIntakeOptions =
      sortIntakeOptionsByCreatedAt(tmpIntakeOptions)

    // console.log('sortedTmpIntakeOptions', sortedTmpIntakeOptions)
    setIntakeOptions([
      {
        ...IntakeOptionShowAll,
        name: enterpriseToggle ? 'Cohort' : 'Hexamester',
      },
      ...sortedTmpIntakeOptions,
    ])
  }, [enterpriseToggle])

  const fetchArchitectures = useCallback(
    async (silent: boolean = false) => {
      if (!silent) {
        setIsFetchingArchitectures(true)
      }
      try {
        const res = await getArchForTable()
        if (res && res.courses) {
          // only set one course per client to dict by default
          const tmpFilteredCourses: FilteredCourseDict = {}
          res.courses.forEach((course) => {
            if (!tmpFilteredCourses[course.clientId]) {
              tmpFilteredCourses[course.clientId] = course
            }
          })
          refFilteredCourses.current = tmpFilteredCourses
          // for cache
          refCourses.current = res.courses
          setCoursesForTable(res.courses)
        }
      } catch (error) {
        ToastUtil.error('Failed to fetch architectures')
        refCourses.current = []
      } finally {
        setIsFetchingArchitectures(false)
      }
    },
    [getArchForTable],
  )

  const fetchReloadedClient = useCallback(
    async (targetClientId: string) => {
      try {
        // console.log('currentClientId', refCurrentClientId.current)
        // console.log('targetClientId', targetClientId)
        const currentClientId = refCurrentClientId.current
        if (!currentClientId) {
          return null
        }
        const reloadedClient = await getArchClient(targetClientId)
        // update selected client if it's the same
        if (currentClientId === targetClientId) {
          // console.log('reloadedClient', reloadedClient)
          refSelectedClient.current = reloadedClient
        }
        // console.log('client', client)
        // update clients
        const tmpClients = clients.map((c) => {
          if (c.id === targetClientId) {
            return reloadedClient
          }
          return c
        })
        // console.log('tmpClients', tmpClients)
        setClients(tmpClients)
        updateProgramOptions()
        updateIntakeOptions()
      } catch (error) {
        ToastUtil.error('Failed to fetch client')
        return null
      }
    },
    [
      clients,
      refCurrentClientId,
      getArchClient,
      updateIntakeOptions,
      updateProgramOptions,
    ],
  )

  const fetchClients = useCallback(
    async (silent: boolean = false) => {
      if (!silent) {
        setIsFetchingClients(true)
      }
      try {
        const clients = await getArchClients()
        setClients(clients)

        // Set client options
        const allOptions = clients.map((client) => ({
          id: client.id,
          name: client.name,
          value: client.id,
          default: client.id === refCurrentClientId.current,
        }))

        // sort by alphabet
        allOptions.sort((a, b) => {
          if (a.name < b.name) {
            return -1
          }
          if (a.name > b.name) {
            return 1
          }
          return 0
        })

        setClientOptions([ClientOptionShowAll, ...allOptions])

        await fetchArchitectures(silent)

        // console.log('clients', clients)
        // pre-select client if clientId is provided
        const currentClientId = refCurrentClientId.current
        if (currentClientId) {
          const client = clients.find((client) => client.id === currentClientId)
          // console.log('client', client)
          if (client) {
            refSelectedClient.current = client
            if (!silent) {
              setSelectedClient(client)
            }
            filterTable(client.id)
          }
        }
        updateProgramOptions()
        updateIntakeOptions()
      } catch (error) {
        ToastUtil.error('Failed to fetch clients')
      } finally {
        setIsFetchingClients(false)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [refCurrentClientId, getArchClients],
  )

  const filterTable = (clientId: string) => {
    const tmpCourses = refCourses.current.filter(
      (course) => course.clientId === clientId,
    )
    setCoursesForTable(tmpCourses)
  }

  const onSelectClient = (
    item: DropDownItem,
    options?: { forceViewMode?: boolean },
  ) => {
    const value = item.value
    const nextAdminMenu = options?.forceViewMode
      ? AdminMenu.View
      : selectedAdminMenu

    if (value === '0') {
      // label
      return
    } else if (value === 'all') {
      setSelectedClient(null)
      refSelectedClient.current = null
      refCurrentClientId.current = undefined

      // change the last path of url
      window.history.replaceState(null, '', `/architectures`)
      // go back to view mode
      setSelectedAdminMenu(AdminMenu.View)
      // reset courses
      setCoursesForTable(refCourses.current)

      // reset selected program and intake
      setSelectedProgram(null)
      refSelectedProgram.current = null
      setSelectedIntake(null)
      refSelectedIntake.current = null

      // reset dropdowns
      refDropDownMenuPrograms.current?.setSelectedItemId(
        ProgramOptionShowAll.id,
      )
      refDropDownMenuIntakes.current?.setSelectedItemId(IntakeOptionShowAll.id)
    } else {
      // selected client
      const client = clients.find((client) => client.id === value)
      // console.log('client', client)
      if (client) {
        refSelectedClient.current = client
        setSelectedClient(client)
        filterTable(client.id)
        if (options?.forceViewMode) {
          setSelectedAdminMenu(AdminMenu.View)
        }

        // reset selected program and intake
        setSelectedProgram(null)
        refSelectedProgram.current = null
        setSelectedIntake(null)
        refSelectedIntake.current = null
      }
    }

    updateProgramOptions()
    updateIntakeOptions()
    updateCourses()

    // change the last path of url with client id silently
    if (value !== 'all') {
      const mode = nextAdminMenu === AdminMenu.Manage ? '/manage' : ''
      window.history.replaceState(null, '', `/architectures/${value}${mode}`)
      refCurrentClientId.current = value
    }
  }

  const updateCourses = () => {
    // filter courses by selected client, program, or intake
    const clientId = refSelectedClient.current?.id
    const programName = refSelectedProgram.current?.name
    const intakeName = refSelectedIntake.current?.name
    // console.log('updateCourses')
    // console.log('clientId:', clientId)
    // console.log('programName:', programName)
    // console.log('intakeName:', intakeName)

    const allCourses = refCourses.current
    let tmpCourses: ArchCourseForTable[] = []
    if (clientId && programName && intakeName) {
      tmpCourses = allCourses.filter(
        (course) =>
          course.clientId === clientId &&
          course.programName === programName &&
          course.intakeName === intakeName,
      )
    } else if (clientId && programName) {
      tmpCourses = allCourses.filter(
        (course) =>
          course.clientId === clientId && course.programName === programName,
      )
    } else if (clientId && intakeName) {
      tmpCourses = allCourses.filter(
        (course) =>
          course.clientId === clientId && course.intakeName === intakeName,
      )
    } else if (clientId) {
      tmpCourses = allCourses.filter((course) => course.clientId === clientId)
    } else if (programName) {
      tmpCourses = allCourses.filter(
        (course) => course.programName === programName,
      )
    } else if (intakeName) {
      tmpCourses = allCourses.filter(
        (course) => course.intakeName === intakeName,
      )
    } else {
      tmpCourses = allCourses
    }
    // console.log('tmpCourses', tmpCourses)
    setCoursesForTable(tmpCourses)
  }

  const onSelectProgram = (item: DropDownItem) => {
    const value = item.value
    if (value === '0') {
      return
    } else if (value === 'all') {
      setSelectedProgram(null)
      refSelectedProgram.current = null
    } else {
      const program = { id: item.id, name: item.name } as ArchProgram
      setSelectedProgram(program)
      refSelectedProgram.current = program
    }

    // reset intake options
    setSelectedIntake(null)
    refSelectedIntake.current = null
    // reset dropdown
    refDropDownMenuIntakes.current?.setSelectedItemId(IntakeOptionShowAll.id)

    updateCourses()
  }

  const onSelectIntake = (item: DropDownItem) => {
    const value = item.value
    if (value === '0') {
      return
    } else if (value === 'all') {
      setSelectedIntake(null)
      refSelectedIntake.current = null
    } else {
      const intake = { id: item.id, name: item.name } as ArchIntake
      setSelectedIntake(intake)
      refSelectedIntake.current = intake
    }
    updateCourses()
  }

  const renderSelectedOptions = () => (
    <p className="arch-subheading">
      {getArchBreadcrumb(
        enterpriseToggle,
        selectedClient,
        selectedProgram,
        selectedIntake,
      )}
    </p>
  )

  const onShowCourseDetails = useCallback(
    (data: { course: ArchCourseForTable }) => {
      setCourseDetails(data.course)
      setShowCourseDetails(true)
    },
    [],
  )

  const onShowCopy = useCallback(
    async (data: { type: ArchActionType; ids: string[] }) => {
      // In UNSW Online mode, program copies go directly to the UNSW Online client — no modal needed
      if (!enterpriseToggle && data.type === ArchActionType.Program) {
        const unswClient = clients.find((c) => isUnswOnlineClient(c))
        if (!unswClient) {
          ToastUtil.error('UNSW Online client not found')
          return
        }
        try {
          const cloned = await cloneArchProgram(data.ids[0], unswClient.id)
          Emitter.emit(Events.CopiedArchObject, {
            clientId: unswClient.id,
            programId: cloned.id,
          })
          Emitter.emit(Events.ReloadArchClient, {
            clientId: unswClient.id,
            updateCourseTable: false,
          })
          ToastUtil.success(
            `${getEntityLabel(
              ArchActionType.Program,
              !enterpriseToggle,
            )} copied successfully`,
          )
        } catch (error) {
          ToastUtil.error(
            `Failed to copy ${getEntityLabel(
              ArchActionType.Program,
              !enterpriseToggle,
            ).toLowerCase()}`,
          )
        }
        return
      }

      // reset copy ids
      setCopyProgramId(undefined)
      setCopyIntakeId(undefined)
      setCopyCourseIds(undefined)
      setCopyModuleIds(undefined)
      setCopyTopicIds(undefined)
      setCopyQuizIds(undefined)

      switch (data.type) {
        case ArchActionType.Program:
          setCopyProgramId(data.ids[0])
          break
        case ArchActionType.Intake:
          setCopyIntakeId(data.ids[0])
          break
        case ArchActionType.Course:
          setCopyCourseIds(data.ids)
          break
        case ArchActionType.Module:
          setCopyModuleIds(data.ids)
          break
        case ArchActionType.Topic:
          setCopyTopicIds(data.ids)
          break
        case ArchActionType.Quiz:
          setCopyQuizIds(data.ids)
          break
      }
      setShowWidgetArchCopy(true)
    },
    [clients, cloneArchProgram, enterpriseToggle],
  )

  const onShowEditTopic = useCallback(
    (data: { topic: ArchTopic; position: { x: number; y: number } }) => {
      setEditTopic(data.topic)
      setEditTopicPosition(data.position)
      setShowWidgetEditTopic(true)
    },
    [],
  )

  const onClickManageClient = () => {
    setShowManageClient(true)
  }

  const onAddedClient = (client: ArchClient) => {
    setClients([...clients, client])
    setClientOptions([
      ...clientOptions,
      { id: client.id, name: client.name, value: client.id },
    ])
  }

  const onRenamedClient = (client: ArchClient) => {
    // emit event to update client name in other components
    Emitter.emit(Events.RenamedArchClient, { client })

    const tmpClients = clients.map((c) => {
      if (c.id === client.id) {
        return {
          ...c,
          ...client,
        }
      }
      return c
    })
    setClients(tmpClients)

    if (
      refSelectedClient.current?.id === client.id ||
      selectedClient?.id === client.id
    ) {
      const renamedSelectedClient =
        tmpClients.find((c) => c.id === client.id) ?? null
      refSelectedClient.current = renamedSelectedClient
      setSelectedClient(renamedSelectedClient)
    }

    setClientOptions(
      clientOptions.map((option: any) => {
        if (option.id === client.id) {
          return { id: client.id, name: client.name, value: client.id }
        }
        return option
      }),
    )

    // update current courses for renamed client
    setCoursesForTable(
      courses.map((course) => {
        if (course.clientId === client.id) {
          return {
            ...course,
            clientName: client.name,
          }
        }
        return course
      }),
    )
  }

  const onShowUploadExcel = useCallback((data: { intakeId: string }) => {
    setShowWidgetUpload(true)
    setUploadIntakeId(data.intakeId)
  }, [])

  const onReloadClient = useCallback(
    async (data: { clientId: string; updateCourseTable: boolean }) => {
      // console.log('onReloadClient', data)
      await fetchReloadedClient(data.clientId)
      if (data.updateCourseTable === true) {
        await fetchArchitectures(true)
      }
    },
    [fetchArchitectures, fetchReloadedClient],
  )

  const onShowUnassignedAssignments = useCallback(
    (data: { courseId: string }) => {
      setSelectedCourseId(data.courseId)
      setShowUnassignedAssignments(true)
    },
    [],
  )

  useEffect(() => {
    if (isLogged) {
      fetchClients()
    }
  }, [isLogged, fetchClients])

  // Deep-link: auto-select a program on mount when ?programId is in the URL.
  useEffect(() => {
    const targetId = refDeepLinkProgramId.current
    if (!targetId) return
    const option = programOptions.find((p: { id: string }) => p.id === targetId)
    if (!option) return
    refDeepLinkProgramId.current = null
    onSelectProgram(option)
    refDropDownMenuPrograms.current?.setSelectedItemId(option.id)
    // onSelectProgram is intentionally omitted: including it would cause an infinite loop
    // because onSelectProgram updates state that re-derives programOptions.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [programOptions])

  useEffect(() => {
    Emitter.on(Events.ShowArchCourseDetails, onShowCourseDetails)
    Emitter.on(Events.ShowArchCopy, onShowCopy)
    Emitter.on(Events.ShowArchEditTopic, onShowEditTopic)
    Emitter.on(Events.UploadArchExcel, onShowUploadExcel)
    Emitter.on(Events.ReloadArchClient, onReloadClient)
    Emitter.on(Events.ShowUnassignedAssignments, onShowUnassignedAssignments)
    return () => {
      Emitter.off(Events.ShowArchCourseDetails, onShowCourseDetails)
      Emitter.off(Events.ShowArchCopy, onShowCopy)
      Emitter.off(Events.ShowArchEditTopic, onShowEditTopic)
      Emitter.off(Events.UploadArchExcel, onShowUploadExcel)
      Emitter.off(Events.ReloadArchClient, onReloadClient)
      Emitter.off(Events.ShowUnassignedAssignments, onShowUnassignedAssignments)
    }
  }, [
    onReloadClient,
    onShowCopy,
    onShowCourseDetails,
    onShowEditTopic,
    onShowUnassignedAssignments,
    onShowUploadExcel,
  ])

  const isLoadingArch = isFetchingClients || isFetchingArchitectures
  const filteredProgramName = selectedProgram?.name
  const filteredIntakeName = selectedIntake?.name
  const onFetchClients = () => fetchClients(true)

  return (
    <>
      <SidebarLayout>
        <div className={StyleUtil.containerNoScroll}>
          <div className="flex flex-row items-center">
            <p className={StyleUtil.headline}>
              Choose a program architecture to view or edit
            </p>
            <label
              className="ml-auto flex items-center"
              style={{ fontSize: '14px', fontWeight: 'var(--fw400)' }}
            >
              <ArchSwitch
                $checked={enterpriseToggle}
                checked={enterpriseToggle}
                onChange={(e) => setEnterpriseToggle(e.target.checked)}
                color="default"
              />
              <span style={{ display: 'inline-block', width: '70px' }}>
                {enterpriseToggle ? 'Enterprise' : 'UNSWO'}
              </span>
            </label>
          </div>
          <div className="flex h-[100%] flex-col pb-10 pt-8">
            <div className="mt-4 flex h-full flex-col gap-[24px]">
              <div className="flex flex-row">
                {renderSelectedOptions()}
                {isAdmin && enterpriseToggle && (
                  <>
                    <div className="grow" />
                    <button
                      className={StyleUtil.buttonPrimaryUnsw}
                      onClick={onClickManageClient}
                    >
                      Edit client(s)
                    </button>
                  </>
                )}
              </div>
              <div
                className="mentem-scrollbar-2 overflow-x-auto overflow-y-hidden"
                id="business-happens-here"
              >
                <div className="flex min-w-[955px] flex-col gap-[24px]">
                  <div id="select-boxes-row">
                    {!isFetchingClients &&
                      !isFetchingArchitectures &&
                      clients.length > 0 && (
                        <div className="flex flex-row items-start gap-[12px]">
                          {enterpriseToggle && (
                            <div className="w-[20%]">
                              <DropDownMenu
                                items={clientOptions}
                                onSelected={onSelectClient}
                                ref={refDropDownMenuClients}
                                className="arch-page-dropdown"
                              />
                            </div>
                          )}
                          <div
                            className={enterpriseToggle ? 'w-[60%]' : 'w-[50%]'}
                          >
                            <DropDownMenu
                              items={programOptions}
                              onSelected={onSelectProgram}
                              className={architectureDropdownClassName}
                              ref={refDropDownMenuPrograms}
                            />
                          </div>
                          <div
                            className={enterpriseToggle ? 'w-[20%]' : 'w-[50%]'}
                          >
                            <DropDownMenu
                              items={intakeOptions}
                              onSelected={onSelectIntake}
                              ref={refDropDownMenuIntakes}
                              className={architectureDropdownClassName}
                            />
                          </div>
                        </div>
                      )}
                  </div>
                  <div id="table-row" className="min-h-[50vh]">
                    {selectedAdminMenu === AdminMenu.View && (
                      <ArchReadOnlyView
                        courses={courses}
                        isLoading={isLoadingArch}
                        filteredProgramName={filteredProgramName}
                        filteredIntakeName={filteredIntakeName}
                        isEnterprise={enterpriseToggle}
                      />
                    )}
                    {isAdmin && selectedAdminMenu === AdminMenu.Manage && (
                      <ArchManageView
                        client={selectedClient}
                        isLoading={isLoadingArch}
                        filteredProgramName={filteredProgramName}
                        filteredIntakeName={filteredIntakeName}
                        isEnterprise={enterpriseToggle}
                        onUpdateProgram={onFetchClients}
                        onUpdateIntake={onFetchClients}
                      />
                    )}
                  </div>
                </div>
              </div>
              {isAdmin && selectedClient && (
                <div className="flex flex-row">
                  <div className="grow" />
                  <button
                    className={StyleUtil.buttonBlackWhite}
                    data-testid={
                      selectedAdminMenu === AdminMenu.View
                        ? 'btn-manage-architecture'
                        : 'btn-save-and-exit-architecture'
                    }
                    onClick={() => {
                      if (selectedClient) {
                        if (selectedAdminMenu === AdminMenu.View) {
                          navigate(
                            `/architectures/${selectedClient.id}/manage`,
                            {
                              state: { isEnterprise: enterpriseToggle },
                            },
                          )
                        } else {
                          // save and exit
                          Emitter.emit(Events.ArchSaveAndExit, {})
                          navigate(`/architectures/${selectedClient.id}`, {
                            state: { isEnterprise: enterpriseToggle },
                          })
                        }
                      } else {
                        navigate(`/architectures`, {
                          state: { isEnterprise: enterpriseToggle },
                        })
                      }

                      // toggle admin menu
                      setSelectedAdminMenu(
                        selectedAdminMenu === AdminMenu.View
                          ? AdminMenu.Manage
                          : AdminMenu.View,
                      )
                    }}
                  >
                    {selectedAdminMenu === AdminMenu.View
                      ? 'Manage'
                      : 'Save and exit'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </SidebarLayout>
      {showCourseDetails && courseDetails && (
        <WidgetArchCourseDetailsView
          course={courseDetails}
          isEnterprise={enterpriseToggle}
          onClose={() => setShowCourseDetails(false)}
        />
      )}
      {/* Widgets */}
      {showManageClient && (
        <WidgetArchManageClient
          clients={clients}
          onClose={() => setShowManageClient(false)}
          onAdded={onAddedClient}
          onRenamed={onRenamedClient}
        />
      )}
      {showWidgetArchCopy && (
        <WidgetArchCopy
          programId={copyProgramId}
          intakeId={copyIntakeId}
          courseIds={copyCourseIds}
          moduleIds={copyModuleIds}
          topicIds={copyTopicIds}
          quizIds={copyQuizIds}
          isEnterprise={enterpriseToggle}
          onClose={() => {
            setShowWidgetArchCopy(false)
          }}
        />
      )}
      {showWidgetEditTopic && editTopic && editTopicPosition && (
        <WidgetArchEditTopic
          topic={editTopic}
          position={editTopicPosition}
          onClose={() => {
            setShowWidgetEditTopic(false)
          }}
        />
      )}
      {showWidgetUpload && uploadInakeId && (
        <WidgetArchUpload
          intakeId={uploadInakeId}
          placeholder="Drag file here to upload "
          onClose={() => {
            setShowWidgetUpload(false)
          }}
        />
      )}
      {showUnassignedAssignments && (
        <WidgetUnassignedAssessments
          onClose={() => setShowUnassignedAssignments(false)}
          courseId={selectedCourseId || ''}
        />
      )}
    </>
  )
}

export default ArchitecturePage
